import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import { serviceConsumer } from "../../../../network/ServiceConsumer";
import "./collaboratorPicker.css";

// Add an adjudicator, musician or examiner to an event (MC2 card 930d3ac0;
// Devin 2026-10-26: "type to autocomplete, start showing options, and an add
// button. simple, clean", and the same for musicians and examiners). Type,
// pick one, Add, then the next. Adjudicators come from the Feis.link list (the
// CLRG directory) and show their account status, never their email.
//
// type: "adjudicator" | "musician" | "examiner"; kind: "feis" (feis and
// Oireachtas) or "grade". onAddNew: a button for someone not on the list.
// allowEmail (admin only): an adjudicator who has no email needs one to be
// added (every judge's first booking sends their invitation). The admin app carries a verbatim copy of this file.

const STATUS = {
  active: "Has an account",
  invited: "Invited",
  email: "Will be invited",
  none: "No email yet",
};
const TITLE = { adjudicator: "Add an adjudicator", musician: "Add a musician", examiner: "Add an examiner" };
const MAX_SHOWN = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fold = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const nameOf = (p) => `${p?.fname || ""} ${p?.lname || ""}`.trim() || p?.name || "";
const initialsOf = (p) => `${(p?.fname || nameOf(p)).charAt(0)}${(p?.lname || "").charAt(0)}`.toUpperCase();

// every typed word starts a word of the name ("mai bre" finds Máire Breen);
// names that start with the typed text come first
function matches(list, query) {
  const words = fold(query).split(" ").filter(Boolean);
  if (!words.length) return [];
  const q = words.join(" ");
  return list
    .map((p) => ({ p, key: fold(nameOf(p)) }))
    .filter(({ key }) => {
      const parts = key.split(" ");
      return words.every((w) => parts.some((part) => part.startsWith(w)));
    })
    .sort((a, b) => (b.key.startsWith(q) - a.key.startsWith(q)) || a.key.localeCompare(b.key))
    .slice(0, MAX_SHOWN)
    .map(({ p }) => p);
}

const CollaboratorPicker = ({ open, type = "adjudicator", eventId, kind = "feis", onClose, onAdded, onAddNew, allowEmail = false }) => {
  const base = process.env.REACT_APP_BASE_URL;
  // examiners are booked on grade exams; adjudicators on either; musicians on a feis
  const root = type === "examiner" || (type === "adjudicator" && kind === "grade") ? "grade-exam" : "people";
  const judges = type === "adjudicator";
  const [all, setAll] = useState([]);
  const [onEvent, setOnEvent] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(""); // the confirmation under the box
  const [email, setEmail] = useState(""); // admin: for a judge with no email yet
  const [broken, setBroken] = useState(new Set()); // photos that didn't load: initials instead
  // who's already on the event must be known before anyone is added (else
  // everyone would look addable); reload retries both lists
  const [eventKnown, setEventKnown] = useState(!eventId);
  const [reload, setReload] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true);
    setError("");
    Promise.all([
      // the list is platform-wide (people); adding and the event's own list per route
      serviceConsumer("GET", `${base}/people/${type}`),
      eventId ? serviceConsumer("GET", `${base}/${root}/${eventId}/${type}s`) : null,
    ])
      .then(([list, current]) => {
        if (!live) return;
        setAll(list?.people || []);
        setOnEvent(new Set((current?.people || []).map((p) => String(p._id))));
        setEventKnown(true);
      })
      .catch(() => {
        if (!live) return;
        setEventKnown(false);
        setError("The list couldn't be loaded.");
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [open, base, root, type, eventId, reload]);

  // the cursor waits in the box: once the list has loaded (the box is
  // disabled until then) and the dialog has taken focus
  useEffect(() => {
    if (!open || loading) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, loading]);

  const options = useMemo(() => (chosen ? [] : matches(all, query)), [all, query, chosen]);
  useEffect(() => setActive(0), [query]);
  // keyboard moves keep the highlighted match in view
  useEffect(() => {
    const id = options[active] && options[active]._id;
    if (id) document.getElementById(`ap-opt-${id}`)?.scrollIntoView({ block: "nearest" });
  }, [active, options]);

  const choose = (p) => {
    if (onEvent.has(String(p._id))) return;
    setChosen(p);
    setQuery(nameOf(p));
    setEmail("");
    setError("");
  };
  const typeName = (value) => {
    setQuery(value);
    setChosen(null);
    setEmail("");
    setError("");
    setAdded("");
  };
  const onKey = (e) => {
    if (!options.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % options.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + options.length) % options.length); }
    if (e.key === "Enter") { e.preventDefault(); choose(options[active]); }
  };

  const needsEmail = allowEmail && judges && chosen && (chosen.accountStatus || "none") === "none";
  const add = async () => {
    if (!chosen || busy) return;
    const typed = needsEmail ? email.trim() : "";
    if (needsEmail && !EMAIL_RE.test(typed)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await serviceConsumer("POST", `${base}/${root}/${type}`, {
        eventId,
        peopleId: chosen._id,
        ...(typed ? { email: typed } : {}),
      });
      if (!res?.success) throw Object.assign(new Error("add"), { messages: res?.message });
      setOnEvent((s) => new Set(s).add(String(chosen._id)));
      if (res.inviteError) setError(`${nameOf(chosen)} was added, but the invitation wasn't sent: ${res.inviteError}`);
      setAdded(`${nameOf(chosen)} added${typed && !res.inviteError ? ", and their invitation is on its way" : ""}.`);
      if (onAdded) onAdded(chosen);
      setChosen(null);
      setQuery("");
      setEmail("");
      inputRef.current?.focus();
    } catch (err) {
      setError(typeof err?.messages === "string" && err.messages !== "ServerError" ? err.messages : "That wasn't added. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const showList = !chosen && query.trim().length > 0;
  // not while an add is saving: its result (and any invitation error) shows here
  const close = () => !busy && onClose && onClose();
  return (
    <Modal open={open} onClose={close} className="ap-modal" disableAutoFocus>
      <div className="ap-box" role="dialog" aria-labelledby="ap-title">
        <div className="ap-head">
          <h2 id="ap-title" className="ap-title">{TITLE[type] || "Add"}</h2>
          <button type="button" className="ap-x" aria-label="Close" onClick={close} disabled={busy}>×</button>
        </div>
        <input
          ref={inputRef}
          className="ap-input"
          type="search"
          autoComplete="off"
          placeholder={loading ? "Loading the list…" : "Start typing a name"}
          disabled={loading || !eventKnown}
          value={query}
          onChange={(e) => typeName(e.target.value)}
          onKeyDown={onKey}
          role="combobox"
          aria-expanded={showList}
          aria-controls="ap-options"
          aria-activedescendant={showList && options[active] ? `ap-opt-${options[active]._id}` : undefined}
        />
        {showList && (
          <ul className="ap-options" id="ap-options" role="listbox">
            {options.length ? options.map((p, i) => {
              const isAdded = onEvent.has(String(p._id));
              return (
                <li
                  key={p._id}
                  id={`ap-opt-${p._id}`}
                  role="option"
                  aria-selected={i === active}
                  aria-disabled={isAdded}
                  className={`ap-option${i === active ? " on" : ""}${isAdded ? " added" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                >
                  <span className="ap-avatar" aria-hidden="true">
                    {p.photo && !broken.has(p._id)
                      ? <img src={p.photo} alt="" onError={() => setBroken((b) => new Set(b).add(p._id))} />
                      : initialsOf(p)}
                  </span>
                  <span className="ap-who">
                    <span className="ap-name">{nameOf(p)}</span>
                    {p.location && <span className="ap-place">{p.location}</span>}
                  </span>
                  {isAdded
                    ? <span className="ap-status added">Added</span>
                    : judges && <span className={`ap-status ${p.accountStatus || "none"}`}>{STATUS[p.accountStatus] || STATUS.none}</span>}
                </li>
              );
            }) : (
              <li className="ap-empty">
                No one by that name.{judges && !onAddNew ? " Not on the list? Contact Feis.link." : ""}
              </li>
            )}
          </ul>
        )}
        {needsEmail && (
          <div className="ap-email">
            <label className="ap-label" htmlFor="ap-email">Their email</label>
            <input id="ap-email" className="ap-input" type="email" autoComplete="off" placeholder="name@example.com"
              value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} />
            <p className="ap-note">No email on file yet. Their account invitation goes to this address when you add them.</p>
          </div>
        )}
        {!allowEmail && judges && chosen && (chosen.accountStatus || "none") === "none" && (
          <p className="ap-note">No email on file yet: contact Feis.link so their invitation can go out.</p>
        )}
        {added && <p className="ap-ok" role="status">✓ {added}</p>}
        {error && (
          <p className="ap-error" role="alert">
            {error}
            {!eventKnown && !loading && (
              <> <button type="button" className="ap-retry" onClick={() => setReload((n) => n + 1)}>Try again</button></>
            )}
          </p>
        )}
        <div className="ap-acts">
          {onAddNew && (
            <button type="button" className="ap-link" onClick={onAddNew}>Add someone new</button>
          )}
          <button type="button" className="ap-btn ghost" onClick={close} disabled={busy}>Done</button>
          <button type="button" className="ap-btn primary" onClick={add} disabled={!chosen || busy || !eventKnown || (needsEmail && !EMAIL_RE.test(email.trim()))}>
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CollaboratorPicker;
